import {makeAutoObservable, observable, runInAction} from "mobx";
import {injectable, inject} from "inversify";
import {Bindings} from "data/constants/bindings";
import type {IJSONProvider} from "data/providers/json/json.provider";
import {PlayerStatActionEnum, PlayerCategory} from "data/enums";

export interface IHittingStats {
	atBats: number;
	caughtStealing: number;
	cycle: number;
	double: number;
	gidp: number;
	grandSlam: number;
	hbp: number;
	hits: number;
	homeRun: number;
	rbi: number;
	run: number;
	single: number;
	steal: number;
	strikeout: number;
	triple: number;
	walk: number;
}

export interface IPitchingStats {
	batterHit: number;
	completeGame: number;
	earnedRuns: number;
	hitsAllowed: number;
	inningsPitched: number;
	losses: number;
	noHitter: number;
	out: number;
	perfectGame: number;
	qualityStart: number;
	scorelessInning: number;
	shutout: number;
	strikeout: number;
	successfulPickOff: number;
	walks: number;
	wildPitch: number;
	win: number;
}

export interface IContestStat {
	playerId: number;
	rank: number;
	tournamentId: number;
	opponentSquadId: number;
	selectedPercentage: number;
	projectedPoints: number;
	pointsMultiplier: number;
	fantasyPoints: number;
	isRemoved: boolean;
	isLocked: boolean;
	category: PlayerCategory;
	action: PlayerStatActionEnum;
	hitting: IHittingStats;
	pitching: IPitchingStats;
}

export interface IContestPlayerStatsStore {
	get stats(): IContestStat[];

	get statsHistory(): Record<number, IContestStat[]>;

	fetchStatsSafe(contestId: number): Promise<void>;

	fetchStatsLive(contestId: number): Promise<void>;

	getPlayerStats: (playerId: number) => IContestStat | undefined;
}

@injectable()
export class ContestPlayerStatsStore implements IContestPlayerStatsStore {
	@observable _stats: IContestStat[] = [];
	@observable _statsByContestId: Record<number, IContestStat[]> = {};
	@observable _liveContestId: number | null = null;

	constructor(@inject(Bindings.JSONProvider) private _jsonProvider: IJSONProvider) {
		makeAutoObservable(this);
	}

	get stats(): IContestStat[] {
		return this._stats || [];
	}

	get statsHistory(): Record<number, IContestStat[]> {
		return this._statsByContestId;
	}

	getPlayerStats = (playerId: number) => {
		const contestStats = this._stats || [];
		return contestStats.find((it) => it.playerId === playerId);
	};

	async fetchStatsSafe(contestId: number) {
		if (this._statsByContestId[contestId]) {
			return;
		}
		const response = await this._jsonProvider.contest_player_stats(contestId);

		runInAction(() => {
			this._stats = response.data.items;
			this._statsByContestId = {
				...this._statsByContestId,
				[contestId]: response.data.items,
			};
		});
	}

	async fetchStatsLive(contestId: number) {
		if (!contestId) {
			return;
		}
		const response = await this._jsonProvider.contest_player_stats(contestId);

		runInAction(() => {
			this._stats = response.data.items;
			this._statsByContestId = {
				...this._statsByContestId,
				[contestId]: response.data.items,
			};
		});
	}
}
